import * as React from "react";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import {
  ClickAwayListener,
  Typography,
  Popover,
  FormControl,
  InputAdornment,
  Input,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  ListItemButton,
} from "@mui/material";
import { useHistory } from "react-router-dom";
import { StyledFormControl } from "../../../../components/StyledComponents/Inputs/Inputs.js";
import { useAuth0 } from "@auth0/auth0-react";
import styled from "@emotion/styled";
import {
  BodyLarge,
  BodyMedium,
  BodySmall,
  BodyXSmall,
  H6HeadlineSmall,
  H7TitleLarge,
  H8TitleMedium,
} from "../../../../components/StyledComponents/Typography/Typography.tsx";
import {
  SecondaryButton,
  StyledIconButton,
} from "../../../../components/StyledComponents/Buttons/AuradineButtons.js";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import DriveFileMoveOutlinedIcon from "@mui/icons-material/DriveFileMoveOutlined";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { useState } from "react";
import { useGetSiteLists } from "../../../../api/queries/useGetSiteLists.ts";
import useMinerPageContext from "../../../context/useMinerPageContext.js";
import useSiteAndGroupTreeContext from "../../../context/useSiteAndGroupTreeContext.js";
import { useMoveDGMember } from "../../../queries/useMoveDGMember.ts";
import { useQueryClient } from "@tanstack/react-query";
import MinerConfigurationDialog from "../../ConfigurationModal/ConfigurationModal.js";
import { CustomTHSForm } from "../../CustomizationModal/CustomizationModal.js";
import { useTheme } from "../../../../ThemeContext/ThemeContext.js";

const StyledPopoverContent = styled(Box)(({ colors }) => ({
  display: "flex",
  flexDirection: "column",
  borderRadius: "1rem",
  backgroundColor: colors?.background?.popover,  
  boxShadow:
    "0px 2px 6px 2px rgba(0, 0, 0, 0.15), 0px 1px 2px 0px rgba(0, 0, 0, 0.3)",
}));


const StyledPopover = styled(Popover,)(({ colors }) => ({
  "& .MuiPaper-root": {
    borderRadius: "1rem", 
    backgroundColor: colors?.background?.popover,  
  },
}));

const StyledHeaderAndFooterBox = styled(Box)`
  padding: 1.5rem;
`;

const GroupOperationsCard = (props) => {
  const {
    open,
    handleClose,
    anchorRef,
    refetch: minerPageRefetch,
    resetTableSelection,
    setTableKey,
  } = props;
  const queryClient = useQueryClient();
  const { colors } = useTheme();
  const history = useHistory();

  const [siteAndGroups, setSiteAndGroups] =
    React.useState();

  //fetching to get userPreferences to set the tableGroup and detailGroup from user object. If not present, we are setting default values
  const [authToken, setAuthToken] = React.useState("");
  const { siteSelectedName, groupSelectedName } =
    useSiteAndGroupTreeContext();
  const {
    selectedSerials,
    groupFilters,
    setSelectedSerials,
  } = useMinerPageContext();
  const { getAccessTokenSilently, isAuthenticated } =
    useAuth0();

  React.useEffect(() => {
    console.log(1);
    const fetchToken = async () => {
      try {
        const token = await getAccessTokenSilently();
        setAuthToken(token);
      } catch (error) {
        console.error(" Error fetching token:", error);
      }
    };
    fetchToken();
  }, [getAccessTokenSilently]);

  const {
    data: siteLists,
    isLoading: isSiteGroupDataLoading,
    isFetching: isSiteGroupDataFetching,
    isFetched: isSiteGroupDataFetched,
    refetch: siteListRefetch,
  } = useGetSiteLists({
    authToken,
    leftPanelOpen: open,
  });

  const { mutate: moveMiners } = useMoveDGMember();

  React.useEffect(() => {
    if (siteLists) {
      if (
        siteLists?.status === "ok" &&
        siteLists?.siteList?.length > 0
      ) {
        const siteList = siteLists?.siteList;
        setSiteAndGroups(siteList);
      }
    }
  }, [siteLists, isSiteGroupDataFetching]);

  const onSitesAndGroupSearch = (e) => {
    if (e.target.value === "") {
      setSiteAndGroups(siteLists?.siteList);
      return;
    } else {
      const sitesData = [...siteAndGroups];
      const foundSite = sitesData.filter((site) => {
        return (
          site?.siteName
            .toLowerCase()
            .includes(e.target.value.toLowerCase()) ||
          (site.groups &&
            site.groups.some((group) =>
              group.DGName.toLowerCase().includes(
                e.target.value.toLowerCase()
              )
            ))
        );
      });

      setSiteAndGroups(foundSite);
    }
  };
  const [searchValue, setSearchValue] = React.useState("");
  const handleSearchChange = (event) => {
    setSearchValue(event.target.value);
  };

  return (
    <StyledPopover
      colors={colors}
      sx={{ zIndex: 9999, borderRadius: "1rem" }}
      open={open}
      anchorEl={anchorRef.current}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
    >
      <ClickAwayListener onClickAway={handleClose}>
        <StyledPopoverContent colors={colors}>
          <StyledHeaderAndFooterBox
            display={"flex"}
            flexDirection={"column"}
            gap={4}
          >
            <H7TitleLarge>Move to:</H7TitleLarge>
            <BodySmall>
              Select which group you would like to transfer
              the miners to.
            </BodySmall>
          </StyledHeaderAndFooterBox>
          <Box
            display="flex"
            flexDirection={"column"}
            padding="0rem 1.5rem"
            className={"search-box"}
          >
            {/* Search Input */}
            <StyledFormControl colors={colors}
              fullWidth
              sx={{ m: 1 }}
              variant="standard"
            >
              <Input
                id="standard-adornment-amount"
                placeholder="Search groups"
                value={searchValue}
                onChange={(event) => {
                  handleSearchChange(event);
                  onSitesAndGroupSearch(event);
                }}
                sx={{
                  "& .MuiInputBase-input": {
                    color: colors?.text?.primary,
                    "::placeholder": {
                      color: colors?.text?.hint,
                    },
                  },
                  "& .MuiInputBase-inputMultiline": {
                    color: colors?.text?.primary,
                    "::placeholder": {
                      color: colors?.text?.hint,
                    },
                  },
                }}
                startAdornment={
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: colors?.text?.muted }} />
                  </InputAdornment>
                }
                endAdornment={
                  <InputAdornment position="end">
                    <StyledIconButton
                      colors={colors}
                      onClick={() => {
                        setSearchValue("");
                        setSiteAndGroups(
                          siteLists?.siteList
                        );
                      }}
                    >
                      <CloseIcon sx={{ color: colors?.text?.muted }} />
                    </StyledIconButton>
                  </InputAdornment>
                }
              />
            </StyledFormControl>

            {/* Groups List */}
            <Box
              display={"flex"}
              flexDirection={"column"}
              width={"22.5rem"}
              minWidth={"22.5rem"}
              maxHeight={"15rem"}
              minHeight={"15rem"}
              maxWidth={"45rem"}
              overflow={"auto"}
            >
              {siteAndGroups &&
                Array.isArray(siteAndGroups) &&
                siteAndGroups.length > 0 &&
                siteAndGroups.map((site, siteIndex) => (
                  <React.Fragment key={siteIndex}>
                    <H8TitleMedium key={siteIndex}>
                      {site.siteName}
                    </H8TitleMedium>
                    {site.groups &&
                      site.groups.length > 0 &&
                      site.groups.map(
                        (group, groupIndex) => (
                          <React.Fragment key={groupIndex}>
                            <ListItemButton
                              key={groupIndex}
                              disabled={
                                groupSelectedName ===
                                  group.DGName &&
                                siteSelectedName ===
                                  site.siteName
                              }
                              onClick={async () => {
                                moveMiners(
                                  {
                                    serials:
                                      selectedSerials,
                                    sourceSite:
                                      siteSelectedName,
                                    sourceGroup:
                                      groupSelectedName,
                                    destinationSite:
                                      site.siteName,
                                    destinationgroup:
                                      group.DGName,
                                    filter: groupFilters,
                                  },
                                  {
                                    onSuccess: async (
                                      data
                                    ) => {
                                      await new Promise(
                                        (r) =>
                                          setTimeout(r, 300)
                                      );
                                      console.log(
                                        "Move miners success"
                                      );
                                      console.log(
                                        "miner page refetch",
                                        minerPageRefetch
                                      );
                                      await minerPageRefetch().then(
                                        (data) => {
                                          console.log(
                                            "Miner page refetched",
                                            data
                                          );
                                        }
                                      );
                                      await queryClient.invalidateQueries(
                                        {
                                          queryKey: [
                                            "getSiteLists",
                                            authToken,
                                          ],
                                        }
                                      );
                                      await siteListRefetch().then(
                                        (data) => {
                                          console.log(
                                            "Site list refetched",
                                            data
                                          );
                                        }
                                      );
                                      resetTableSelection();
                                      setTableKey(
                                        (prev) => prev + 1
                                      );
                                      handleClose();
                                    },
                                    onError: (error) => {
                                      console.log(
                                        "Move miners error",
                                        error
                                      );
                                      handleClose();
                                    },
                                  }
                                );
                                // resetTableSelection();
                              }}
                              sx={{
                                minHeight: "3rem",
                                maxHeight: "3rem",
                                "&:hover": {
                                  backgroundColor:
                                    colors.hover.muted,
                                  borderRadius: "6.5rem",
                                  cursor: "pointer",
                                },
                              }}
                            >
                              <BodyLarge>{group.DGName}</BodyLarge>
                            </ListItemButton>
                          </React.Fragment>
                        )
                      )}
                  </React.Fragment>
                ))}
            </Box>
          </Box>
          <Box>
            <List
              sx={{
                padding: "0.25rem 1.5rem",
                borderTop: "2px solid #CAC4D0",
              }}
            >
              <ListItem
                sx={{
                  display: "flex",
                  height: "3rem",
                  minHeight: "3rem",
                }}
              >
                <ListItemButton
                  sx={{
                    display: "flex",
                    height: "3rem",
                    minHeight: "3rem",
                    "&:hover": {
                      backgroundColor: colors.hover.muted,
                    },
                  }}
                  onClick={() => {
                    handleClose();
                    history.push("/sitegroupmanagement");
                  }}
                >
                  <BodyLarge> Manage groups</BodyLarge>
                </ListItemButton>
                <ArrowRightIcon />
              </ListItem>
            </List>
          </Box>
        </StyledPopoverContent>
      </ClickAwayListener>
    </StyledPopover>
  );
};

export default GroupOperationsCard;

export const GroupOperationsTab = (props) => {
  const { colors } = useTheme();
  const {
    refetch: minerPageRefetch,
    resetTableSelection,
    setTableKey,
    userRole,
    setLeftSidePanelOpen,
  } = props;
  const [anchorEl, setAnchorEl] = React.useState(null);
  const { selectedSerials } = useMinerPageContext();
  console.log("userRole", userRole);
  const { siteSelectedName, groupSelectedName } =
    useSiteAndGroupTreeContext();
  const { minerCount } = useMinerPageContext();
  const anchorRef = React.useRef(null);
  const { user } = useAuth0();
  const [open, setOpen] = React.useState(false);
  const navigate = useHistory();
  const handleClick = (event) => {
    event.stopPropagation();
    setOpen((prevOpen) => !prevOpen);
  };
  const siteOrGroupSelected =
    siteSelectedName !== "" ||
    (siteSelectedName !== "" && groupSelectedName !== "");
  const handleClose = () => {
    setOpen(false);
  };
  return (
    <React.Fragment>
      <Tooltip
        title={
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexDirection="row"
            sx={{ textAlign: "center" }}
          >
            <BodySmall sx={{ color: "white" }}>
              Click
              <span
                className="material-symbols-outlined"
                style={{
                  fontSize: "0.875rem",
                  verticalAlign: "middle",
                  color: "white",
                  margin: "0 0.2rem",
                  cursor: "pointer",
                }}
                onClick={() => {
                  setLeftSidePanelOpen(true);
                }}
              >
                right_panel_close
              </span>{" "}
              icon to select group or site
            </BodySmall>
          </Box>
        }
        arrow
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            textAlign: "center",
            // paddingRight: "2rem",
          }}
        >
          <SecondaryButton colors={colors}
            ref={anchorRef}
            onClick={handleClick}
            endIcon={
              <span className="material-symbols-outlined">
                move_group
              </span>
            }
            disabled={
              !siteOrGroupSelected ||
              userRole === "user" ||
              minerCount === 0
            }
          >
            Move
          </SecondaryButton>
        </Box>
      </Tooltip>

      <GroupOperationsCard
        open={open}
        handleClose={handleClose}
        anchorRef={anchorRef}
        refetch={minerPageRefetch}
        resetTableSelection={resetTableSelection}
        setTableKey={setTableKey}
      />
    </React.Fragment>
  );
};
